package gr.tech.site.pages.landings

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Background
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.functions.LinearGradient
import com.varabyte.kobweb.compose.css.functions.linearGradient
import com.varabyte.kobweb.compose.css.functions.toImage
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.sections.RelatedSection
import gr.tech.site.pages.SitePage
import gr.tech.site.styles.Theme
import gr.tech.site.styles.landings.*
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.rgba
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initDehumidifierRebatesPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Commercial Dehumidifier Rebates — Energy Incentives for Indoor Growers",
        description = "Commercial dehumidifiers in cultivation facilities qualify for utility energy incentive programs. Green Rebates identifies every applicable rebate and handles every application.",
        canonicalUrl = "https://greenrebates.com/dehumidifier-rebates",
        ogImage = "/images/cannabiscloseup1.webp"
    ))
}

@Page(SitePage.Landing.DEHUMIDIFIER_REBATES)
@Layout(".components.layouts.PageLayout")
@Composable
fun DehumidifierRebatesPage() {
    Main(
        attrs = Modifier
            .classNames("d-flex", "flex-column")
            .fillMaxSize()
            .role("main")
            .toAttrs()
    ) {

        // HERO
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(560.px)
                .position(Position.Relative)
                .classNames("px-0"),
            contentAlignment = Alignment.Center
        ) {
            Img(
                src = ImageRepo.LEDGrowLights.src,
                alt = ImageRepo.LEDGrowLights.alt,
                attrs = Modifier
                    .fillMaxSize()
                    .objectFit(ObjectFit.Cover)
                    .toAttrs { attr("fetchpriority", "high") }
            )
            Div(
                attrs = Modifier
                    .fillMaxSize()
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .background(
                        Background.of(
                            image = linearGradient(
                                from = Theme.DarkGreen.rgb,
                                to = rgba(2, 67, 66, 0),
                                dir = LinearGradient.Direction.ToRight
                            ).toImage()
                        )
                    )
                    .toAttrs()
            )
            Column(
                modifier = LandingHeroStyle.toModifier().classNames("container", "text-start").fillMaxWidth(),
                horizontalAlignment = Alignment.Start
            ) {
                H3(attrs = LandingHeroEyebrowStyle.toModifier().classNames("text-uppercase", "mb-2").toAttrs()) {
                    Text("Commercial Dehumidifier Rebates for Indoor Cultivators")
                }
                H1(attrs = LandingHeroHeadlineStyle.toModifier().classNames("mb-3").toAttrs()) {
                    Text("Claim Energy Incentives on Commercial Dehumidifiers")
                }
                P(attrs = LandingHeroLeadStyle.toModifier().classNames("mb-4").toAttrs()) {
                    Text("Dehumidification is among the highest energy costs in any indoor cultivation facility. Green Rebates identifies every applicable utility rebate program for commercial dehumidifiers — and handles the entire application process from pre-approval to check.")
                }
                Link(
                    path = "/contact-us",
                    text = "Get Your Free Dehumidifier Rebate Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaPrimaryStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Get a free commercial dehumidifier rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // TYPES OF REBATES — leads with value before process
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(attrs = LandingSectionHeadingStyle.toModifier().classNames("mb-2").toAttrs()) {
                    Text("Types of Dehumidifier Rebates Available")
                }
                P(attrs = LandingSectionSubtitleStyle.toModifier().classNames("mb-5").toAttrs()) {
                    Text("Utility-backed energy incentives for commercial dehumidification in indoor grow and cultivation facilities.")
                }
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-sm-2", "row-cols-lg-4", "g-4").toAttrs()) {
                    listOf(
                        "Commercial Dehumidifier Rebates" to
                            "Direct utility incentives for commercial-grade dehumidification equipment upgrades in qualifying indoor cultivation and greenhouse facilities.",
                        "Energy Efficiency Incentives" to
                            "Grow operations that reduce total facility energy consumption qualify for additional energy efficiency incentive programs beyond standard equipment rebates.",
                        "Grower Incentive Programs" to
                            "State and utility programs specifically designed for indoor growers and horticulture operations often include dedicated funding for humidity control equipment.",
                        "Indoor Grow Operation Rebates" to
                            "Targeted rebate programs for cultivation facilities that manage humidity as part of a broader energy efficiency upgrade — including stacking with LED and HVAC incentives."
                    ).forEachIndexed { index, (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = LandingRebateCardStyle.toModifier().toAttrs()) {
                                when (index) {
                                    0 -> FaDroplet(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    1 -> FaLightbulb(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    2 -> FaSeedling(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    else -> FaFan(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                }
                                H5(attrs = LandingRebateCardTitleStyle.toModifier().classNames("mb-2").toAttrs()) {
                                    Text(title)
                                }
                                P(attrs = LandingRebateCardBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                    Text(desc)
                                }
                            }
                        }
                    }
                }
            }
        }

        // WHO QUALIFIES — image on right
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                Div(attrs = Modifier.classNames("row", "align-items-center", "g-4").toAttrs()) {

                    // Left — copy + checklist + CTA
                    Div(
                        attrs = Modifier
                            .classNames("col-lg-6", "text-lg-start", "text-center")
                            .toAttrs()
                    ) {
                        Div(
                            attrs = Modifier
                                .classNames("d-flex", "flex-column", "align-items-lg-start", "align-items-center", "gap-3")
                                .toAttrs()
                        ) {
                            H2(attrs = LandingSubSectionHeadingStyle.toModifier().toAttrs()) {
                                Text("Who Qualifies for Commercial Dehumidifier Rebates")
                            }
                            P(attrs = LandingMutedBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                Text("From large-scale cannabis cultivation facilities to commercial greenhouses, Green Rebates identifies every energy incentive available for humidity control in commercial grow operations.")
                            }
                            Ul(
                                attrs = Modifier
                                    .classNames("list-unstyled", "d-flex", "flex-column", "gap-2", "text-start", "w-100", "mb-0")
                                    .toAttrs()
                            ) {
                                listOf(
                                    "Indoor cannabis and cultivation facilities upgrading commercial dehumidifiers",
                                    "Greenhouse and horticulture operations with humidity control equipment",
                                    "Grow operations reducing energy consumption through dehumidification upgrades"
                                ).forEach { item ->
                                    Li(attrs = Modifier.classNames("d-flex", "align-items-center", "gap-2").toAttrs()) {
                                        FaCheck(
                                            modifier = LandingCheckmarkStyle.toModifier().classNames("flex-shrink-0"),
                                            size = IconSize.SM
                                        )
                                        Span { Text(item) }
                                    }
                                }
                            }
                            Link(
                                path = "/contact-us",
                                text = "Find Out If Your Facility Qualifies \u2192",
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                modifier = LandingCtaSecondaryStyle.toModifier()
                                    .classNames("mt-2")
                                    .role("button")
                                    .attrsModifier { attr("aria-label", "Find out if your grow facility qualifies for dehumidifier rebates") },
                                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                            )
                        }
                    }

                    // Right — image
                    Div(attrs = Modifier.classNames("col-lg-6").toAttrs()) {
                        Img(
                            src = ImageRepo.Dehumidifier.src,
                            alt = ImageRepo.Dehumidifier.alt,
                            attrs = Modifier.classNames("img-fluid", "rounded-4", "w-100")
                                .toAttrs { attr("loading", "lazy") }
                        )
                    }
                }
            }
        }

        // WHY ACT NOW
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-7")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(attrs = LandingDarkSectionHeadingStyle.toModifier().classNames("mb-3").toAttrs()) {
                    Text("Why Act Now on Dehumidifier Energy Incentives")
                }
                P(attrs = LandingDarkBodyStyle.toModifier().classNames("mb-4", "mx-auto").toAttrs()) {
                    Text("Utility incentive program funding is finite — and demand from indoor growers is increasing every year. Every quarter you delay a dehumidifier upgrade is money permanently left on the table. Green Rebates pre-qualifies your facility and secures your spot in the program before funding runs out.")
                }
                Link(
                    path = "/contact-us",
                    text = "Check Your Dehumidifier Rebate Eligibility \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaOutlineStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Check your commercial dehumidifier rebate eligibility") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // HOW IT WORKS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(attrs = LandingSectionHeadingStyle.toModifier().classNames("mb-2").toAttrs()) {
                    Text("How Green Rebates Secures Your Dehumidifier Incentives")
                }
                P(attrs = LandingSectionSubtitleStyle.toModifier().classNames("mb-5").toAttrs()) {
                    Text("Three steps to maximize energy incentives on commercial dehumidification for your indoor grow or cultivation facility.")
                }
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-3", "g-4").toAttrs()) {
                    listOf(
                        Triple("1", "Contact Us — We Pre-Qualify Your Facility",
                            "Tell us about your existing dehumidification equipment and utility provider. We determine eligibility and gather everything needed to get started."),
                        Triple("2", "We Handle Every Application",
                            "Our specialists manage all paperwork, submissions, and utility follow-up for your dehumidifier rebate — from pre-approval to final approval. Start to finish."),
                        Triple("3", "You Receive Your Incentive Check",
                            "Approved rebates are paid directly to your facility. No chasing utilities, no administrative burden on your team — just the funding you've earned.")
                    ).forEach { (num, title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "flex-column", "align-items-center", "text-center", "gap-3", "h-100", "py-4", "px-3").toAttrs()) {
                                Div(
                                    attrs = LandingStepCardNumberStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center")
                                        .toAttrs()
                                ) { Text(num) }
                                H4(attrs = LandingStepCardTitleStyle.toModifier().classNames("mb-1").toAttrs()) { Text(title) }
                                P(attrs = LandingStepCardBodyStyle.toModifier().classNames("mb-0").toAttrs()) { Text(desc) }
                            }
                        }
                    }
                }
            }
        }

        // WHY GREEN REBATES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(attrs = LandingDarkSectionHeadingStyle.toModifier().classNames("mb-5").toAttrs()) {
                    Text("Why Cultivators Choose Green Rebates for Dehumidifier Incentives")
                }
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-sm-2", "g-4").toAttrs()) {
                    listOf(
                        "Cultivation-Focused Expertise" to
                            "We specialize in rebate programs for indoor grow operations, not general commercial properties. We know exactly which incentives apply to cultivation facilities and their humidity control equipment.",
                        "Direct Utility Relationships" to
                            "Our working relationships with utility program administrators mean faster pre-approvals and higher payouts for your grow operation's dehumidification upgrade.",
                        "End-to-End Application Management" to
                            "From facility audit to rebate check, we own the entire application process. You never have to chase a single form or utility contact.",
                        "Incentive Stacking" to
                            "We research every available energy efficiency program — including stacking dehumidifier rebates with LED and HVAC incentives — to maximize total funding for your cultivation facility."
                    ).forEach { (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "gap-3", "align-items-start").toAttrs()) {
                                Div(
                                    attrs = LandingCheckIconStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center", "flex-shrink-0")
                                        .toAttrs()
                                ) { FaCheck(size = IconSize.SM) }
                                Div {
                                    H5(attrs = LandingDarkItemTitleStyle.toModifier().classNames("mb-1").toAttrs()) { Text(title) }
                                    P(attrs = LandingDarkItemBodyStyle.toModifier().classNames("mb-0").toAttrs()) { Text(desc) }
                                }
                            }
                        }
                    }
                }
            }
        }

        // TESTIMONIALS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(attrs = LandingSectionHeadingStyle.toModifier().classNames("mb-5").toAttrs()) {
                    Text("What Growers Are Saying About Dehumidifier Rebates")
                }
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-2", "g-4").toAttrs()) {
                    listOf(
                        "Green Rebates found dehumidifier incentives we had no idea existed. They handled everything from pre-qualification to the rebate check. Zero hassle for our team." to
                            "Commercial Cultivation Facility Manager — Michigan",
                        "We recovered a significant portion of our dehumidifier upgrade costs through programs Green Rebates identified. The entire process was faster than I expected." to
                            "Licensed Grower — California"
                    ).forEach { (quote, attribution) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = LandingTestimonialCardStyle.toModifier().toAttrs()) {
                                Span(
                                    attrs = LandingQuoteMarkStyle.toModifier()
                                        .classNames("d-block", "mb-1")
                                        .toAttrs()
                                ) { Text("\u201C") }
                                P(attrs = LandingBodyStyle.toModifier().classNames("fst-italic", "mb-3").toAttrs()) {
                                    Text(quote)
                                }
                                Span(attrs = LandingAttributionStyle.toModifier().toAttrs()) {
                                    Text("— $attribution")
                                }
                            }
                        }
                    }
                }
            }
        }

        RelatedSection(listOf(
            "/article/cash-back-for-growers-a-step-by-step-roadmap-to-utility-incentives" to "Cash Back for Growers: A Step-by-Step Roadmap to Utility Incentives",
            "/article/the-2026-guide-to-cannabis-rebates-how-to-get-paid-for-your-grow-upgrades" to "The 2026 Guide to Cannabis Rebates: How to Get Paid for Your Grow Upgrades",
            "/article/rebates-incentives-for-indoor-growers-what-you-need-to-know" to "Rebates vs. Incentives for Indoor Growers: What You Need to Know",
        ))

        // FINAL CTA
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .id("contact")
                .backgroundColor(Theme.MediumGreen.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(attrs = LandingCtaHeadingStyle.toModifier().classNames("mb-3").toAttrs()) {
                    Text("Ready to Claim Your Dehumidifier Rebates?")
                }
                P(attrs = LandingCtaBodyStyle.toModifier().classNames("mb-4", "mx-auto").toAttrs()) {
                    Text("Don't leave cultivation rebates, indoor grow incentives, or energy efficiency programs unclaimed. Green Rebates finds every available dehumidifier incentive for commercial growers and handles every application — start to finish.")
                }
                Link(
                    path = "/contact-us",
                    text = "Contact Us for a Free Dehumidifier Rebate Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaThirdStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Contact Green Rebates for a free commercial dehumidifier rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }
    }
}
